import React from 'react';
import {
  Button,
  TextField,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  InputLabel,
  FormControl,
  OutlinedInput,
} from '@mui/material';
import moment from 'moment';
import LoadingButton from '@mui/lab/LoadingButton';
import { useHistory } from 'react-router-dom';

type FiltersProps = {
  loading: boolean;
  get_payment_date: { startDate?: string; endDate?: string };
  set_payment_date: React.Dispatch<React.SetStateAction<{ startDate?: string; endDate?: string }>>;
  fetchAPI: () => Promise<void>;
  showFilters: boolean;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  get_parcel_date: { startDate?: string; endDate?: string };
  set_parcel_date: React.Dispatch<React.SetStateAction<{ startDate?: string; endDate?: string }>>;
  ENABLE_ASSIGNED_DATE_FILTER: boolean;
  getStatus: string[];
  setStatus: React.Dispatch<React.SetStateAction<string[]>>;
};

const statusOptions = [
  { value: 'DELIVERED', label: 'Delivered' },
  { value: 'RETURNED', label: 'Returned' },
  { value: 'PARTIALLY_DELIVERED', label: 'Partially Delivered' },
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

export default function Filters({
  loading,
  get_payment_date,
  set_payment_date,
  fetchAPI,
  showFilters,
  setShowFilters,
  get_parcel_date,
  set_parcel_date,
  ENABLE_ASSIGNED_DATE_FILTER,
  getStatus,
  setStatus,
}: FiltersProps) {
  const history = useHistory();

  const handleStatusChange = (event: any) => {
    const {
      target: { value },
    } = event;
    const newStatus = typeof value === 'string' ? value.split(',') : value;
    setStatus(newStatus);

    const params = new URLSearchParams(window.location.search);
    if (newStatus.length > 0) {
      params.set('status_in', newStatus.join(','));
    } else {
      params.delete('status_in');
    }
    history.push({ search: params.toString() });
  };

  return (
    <>
      <div style={{ gap: 12, marginBottom: 12 }} className="flex md:hidden">
        <Button variant="outlined" size="small" onClick={() => setShowFilters((v) => !v)}>
          {showFilters ? 'Hide Filters' : 'Show Filters'}
        </Button>
      </div>

      <div
        style={{
          background: '#f7f9fb',
          borderRadius: 12,
          padding: 20,
          marginBottom: 20,
          boxShadow: '0 2px 8px #e3e8ee',
        }}
        className={`${showFilters ? 'block' : 'hidden'} md:block`}
      >
        <div style={{ fontWeight: 600, fontSize: 18, marginBottom: 16 }}>Search Filters</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'flex-end' }}>
          {ENABLE_ASSIGNED_DATE_FILTER && (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <TextField
                  label="Assigned Start"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={loading}
                  value={get_parcel_date.startDate || ''}
                  onChange={(e) => {
                    if (moment(e.target.value).isValid()) {
                      set_parcel_date((prev) => ({
                        ...prev,
                        startDate: moment(e.target.value).format('YYYY-MM-DD'),
                      }));
                    } else {
                      set_parcel_date((prev) => ({ ...prev, startDate: undefined, endDate: undefined }));
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputRef={(input) => {
                    if (input) {
                      (input as HTMLInputElement).showPicker = (input as HTMLInputElement).showPicker || (() => {});
                    }
                  }}
                  onClick={(e) => {
                    const input = e.target as HTMLInputElement;
                    if (input && typeof input.showPicker === 'function') input.showPicker();
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
                <TextField
                  label="Assigned End"
                  type="date"
                  variant="outlined"
                  size="small"
                  fullWidth
                  disabled={loading || !get_parcel_date.startDate}
                  value={get_parcel_date.endDate || ''}
                  onChange={(e) => {
                    if (moment(e.target.value).isValid()) {
                      set_parcel_date((prev) => ({
                        ...prev,
                        endDate: moment(e.target.value).format('YYYY-MM-DD'),
                      }));
                    } else {
                      set_parcel_date((prev) => ({ ...prev, endDate: undefined }));
                    }
                  }}
                  InputLabelProps={{ shrink: true }}
                  inputRef={(input) => {
                    if (input) {
                      (input as HTMLInputElement).showPicker = (input as HTMLInputElement).showPicker || (() => {});
                    }
                  }}
                  onClick={(e) => {
                    const input = e.target as HTMLInputElement;
                    if (input && typeof input.showPicker === 'function') input.showPicker();
                  }}
                />
              </div>
            </>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <TextField
              label="Payment Start"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading}
              value={get_payment_date.startDate || ''}
              onChange={(e) => {
                if (moment(e.target.value).isValid()) {
                  set_payment_date((prev) => ({
                    ...prev,
                    startDate: moment(e.target.value).format('YYYY-MM-DD'),
                  }));
                } else {
                  set_payment_date((prev) => ({ ...prev, startDate: undefined, endDate: undefined }));
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputRef={(input) => {
                if (input) {
                  (input as HTMLInputElement).showPicker = (input as HTMLInputElement).showPicker || (() => {});
                }
              }}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input && typeof input.showPicker === 'function') input.showPicker();
              }}
            />
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', minWidth: 180 }}>
            <TextField
              label="Payment End"
              type="date"
              variant="outlined"
              size="small"
              fullWidth
              disabled={loading || !get_payment_date.startDate}
              value={get_payment_date.endDate || ''}
              onChange={(e) => {
                if (moment(e.target.value).isValid()) {
                  set_payment_date((prev) => ({
                    ...prev,
                    endDate: moment(e.target.value).format('YYYY-MM-DD'),
                  }));
                } else {
                  set_payment_date((prev) => ({ ...prev, endDate: undefined }));
                }
              }}
              InputLabelProps={{ shrink: true }}
              inputRef={(input) => {
                if (input) {
                  (input as HTMLInputElement).showPicker = (input as HTMLInputElement).showPicker || (() => {});
                }
              }}
              onClick={(e) => {
                const input = e.target as HTMLInputElement;
                if (input && typeof input.showPicker === 'function') input.showPicker();
              }}
            />
          </div>

          <FormControl sx={{ minWidth: 200 }} size="small">
            <InputLabel id="status-multiple-checkbox-label">Status</InputLabel>
            <Select
              labelId="status-multiple-checkbox-label"
              id="status-multiple-checkbox"
              multiple
              value={getStatus}
              onChange={handleStatusChange}
              input={<OutlinedInput label="Status" />}
              renderValue={(selected) =>
                (selected as string[])
                  .map((value) => statusOptions.find((opt) => opt.value === value)?.label || value)
                  .join(', ')
              }
              MenuProps={MenuProps}
              disabled={loading}
            >
              {statusOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <Checkbox checked={getStatus.indexOf(option.value) > -1} />
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div style={{ display: 'flex', gap: 8 }}>
            {loading ? (
              <LoadingButton loading variant="contained" loadingPosition="end" sx={{ minWidth: 100 }}>
                Searching..
              </LoadingButton>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={async () => {
                  await fetchAPI();
                }}
                sx={{ minWidth: 100 }}
              >
                Search
              </Button>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
